import { select } from "@angular-redux/store";
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthData, PlanData } from "sharedFeatures/sharedModels";
import { XeroImport } from "sharedFeatures/sharedModels/XeroImport";
import { SharedPlanService, XeroService } from "sharedFeatures/sharedServices";
import {
    IErrorResponse, IPlanTeamCustomers, isErrorResponse
} from "sharedFeatures/sharedServices/Responses/sharedPlan.index";
import { Utils } from "sharedFeatures/utils";
import { SharedGuiSmallModule } from '../../../../sharedGuiSmall/sharedGuiSmall.module';


@Component({
    selector: 'tab-link',
    template: '<div [@hovered]="hovered" (mouseenter)="mouseIn()" (mouseleave)="mouseOut()" (click)="onSelectFunc()" class="cnt-row linkdiv" attr.data-testid="{{testId}}"><div *ngIf="imgCls" class="{{imgCls}}"></div><div *ngIf="imgCls" [width]="5"></div><div class="txt">{{tabTitle}}</div></div>',
    styleUrls: ['./tab-link.component.less'],
    standalone: true,
    imports: [SharedGuiSmallModule],
    animations: [

        trigger('hovered', [
            state('hovered', style({
                color: '#e1e8ee'
            })),
            state('nothovered', style({
                color: '#a5b1be'
            })),
            state('clicked', style({
                color: 'white'
            })),
            transition('nothovered => hovered', animate('0ms ease-in')),
            transition('hovered => nothovered', animate('0ms ease-out'))
        ])
    ],
})

export class TabLinkComponent {

    @Input()
    tabTitle: string = '';

    @Input()
    private path: string = '';

    imgCls: string = '';

    @Input()
    private defImgCls: string = '';

    @Input()
    private hoverImgCls: string = '';

    @Input()
    private activeImgCls: string = '';

    @Input()
    isXeroBtn: boolean = false;

    @Input()
    testId: string = '';

    hovered: string = 'nothovered';

    private clicked: boolean = false;
    private isInProgress: boolean = false;
    private userID: number = 0;
    private planGUID: string = '';

    @Input()
    private onSelect: Function = this.onSelectDefault;

    private element: ElementRef;

    private router: Router;

    @Output() activate: EventEmitter<any> = new EventEmitter();

    @Input()
    private beforeSelect: Function = this.beforeSelectDefault;

    // Unsubscribe event
    private storeUnsubscribe = new Subject<void>();

    @select(['account', 'authData'])
    readonly authData$!: Observable<AuthData>;

    @select(['plan', 'planData'])
    readonly planData$!: Observable<PlanData>;
    private planData: PlanData = new PlanData();

    @select(['sharedPlan', 'onPlanUpdatedToggle'])
    readonly onPlanUpdatedToggle$!: Observable<boolean>;
    private onPlanUpdatedToggle: boolean = false;

    @select(['sharedPlan', 'onXeroImportToggle'])
    readonly onXeroImportToggle$!: Observable<boolean>;


    constructor( el: ElementRef,
                 r: Renderer2,
                 private chDet: ChangeDetectorRef,
                 private xeroService: XeroService,
                 private sharedService: SharedPlanService,
                 router: Router) {
        this.element = el;
        this.router = router;
    }

    ngOnChanges() {

    }

    ngOnDestroy() {
        // Unsubscribe from the redux store
        this.storeUnsubscribe.next();
        this.storeUnsubscribe.complete();
    }

    getEl(): ElementRef {

        return this.element;
    }

    ngOnInit(): void {

        this.imgCls = this.defImgCls;

        if (this.isXeroBtn)
            this.subscribeReduxStore();
    }

    // Subscribe on the redux store
    private subscribeReduxStore(): void {
        this.authData$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe((authData: AuthData) => {
            if (authData)
            {
                this.userID = authData.userID;
                this.checkImportStatus(true);
            }});

        this.planData$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe((planData: PlanData) => {
                if (planData)
                {
                    this.planData = planData;
                    this.planGUID = planData.planGUID;
                    this.checkImportStatus(true);
                }});

        this.onPlanUpdatedToggle$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => {
                //console.warn('PLAN UPDATED - TAB LINK ' + this.onPlanUpdatedToggle);
                this.onPlanUpdatedToggle = x;
                this.checkImportStatus(this.onPlanUpdatedToggle);
            });

        this.onXeroImportToggle$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => {
                //console.warn('XERO IMPORT STARTED');
                this.isInProgress = x;
                if (this.isInProgress)
                    this.setXeroImportStatus('xeroMenuSpinner');
            });
    }

    checkImportStatus(newImportSaved: boolean) {

        if (!newImportSaved || !this.userID || !this.planGUID || !this.planData.xeroConnected) return;

        this.sharedService.getSharedTeamUsers(this.planGUID).then((data: IPlanTeamCustomers | IErrorResponse) => {
            if (isErrorResponse(data)) return;

            let users = (data as IPlanTeamCustomers).customers;
            if (users.length === 0) return;

            let planOwnerId = users.find(x => x.IsPlanOwner)?.CustomerId;
            let userId = planOwnerId === this.userID ? this.userID : planOwnerId;

            if (!userId)
                console.warn('planOwnerId of ' + this.planGUID + ' was not found');


            this.xeroService.GetXeroImports(userId+'', this.planGUID).then((imports) => {
                let curImports = imports as Array<XeroImport>;

                if (curImports && curImports.length > 0) {

                    let maxId = Math.max.apply(Math, curImports.map(x => x.ID));
                    let lastImport = curImports.find(x => x.ID == maxId)!;
                    this.isInProgress = this.isImportInProgress(lastImport);

                    if (this.isInProgress) {
                        this.setXeroImportStatus('xeroMenuSpinner');
                    } else {
                        let xeroImportStatus = lastImport.HasErrors ? 'xeroMenuWarning' : 'xeroMenuSuccess';

                        if (!Utils.isNullOrEmpty(lastImport.ErrorMessage) &&
                            (lastImport.ErrorMessage.indexOf('xeroToken is null') !== -1 || lastImport.ErrorMessage.indexOf('401') !== -1 || lastImport.ErrorMessage.indexOf('403') !== -1))
                            xeroImportStatus = 'xeroMenuConnection';

                        this.setXeroImportStatus(xeroImportStatus);
                    }

                    this.checkXeroLimits();
                }
            });

        });
    }

    isImportInProgress(xeroImport: XeroImport): boolean {
        return xeroImport.IsCashflowSyncActive;
    }

    checkXeroLimits() {
        this.xeroService.GetXeroLimits(this.userID+'', this.planGUID).then((limits) => {
            if (limits && limits.XeroAuthenticationSuccessfull && limits.XDayLimitRemaining !== null && limits.XDayLimitRemaining === 0)
                this.setXeroImportStatus('xeroMenuPause');
        });
    }

    setXeroImportStatus(currentIcon: string): void {
        this.imgCls = currentIcon;
        this.defImgCls = currentIcon;
        this.hoverImgCls = currentIcon + '-hover';
        this.activeImgCls = currentIcon + '-active';

        if (this.clicked)
            this.imgCls = this.activeImgCls;
    }

    getPath(): string {
        return this.path;
    }

    mouseIn(): void {

        if (this.clicked) return;
        this.imgCls = this.hoverImgCls;
        this.hovered = 'hovered';
    }

    mouseOut(): void {

        if (this.clicked) return;
        this.imgCls = this.defImgCls;
        this.hovered = 'nothovered';
    }

    beforeSelectDefault(): boolean {
        return true;
    }

    onSelectDefault(): void {
        this.doAfterSelect();
    }

    onSelectFunc(): void {
        if (!this.beforeSelect()) return;
        this.onSelect();
    }

    public doAfterSelect(): void {

        const oldClicked = this.clicked;
        this.clicked = true;
        this.hovered = 'clicked';
        this.imgCls = this.activeImgCls;
        if (this.path) {
            this.router.navigate([this.path], { skipLocationChange: true }).then(_ => setTimeout(_ => this.activate.emit(!oldClicked)));
            Utils.GTMRecordUrlChange(this.path);
        }
    }

    setClicked(clicked: boolean): void {

        this.clicked = clicked;
        this.hovered = clicked ? 'clicked' : 'nothovered';
        this.imgCls = clicked ? this.activeImgCls : this.defImgCls;
    }

    public makeActive(): void {
        this.onSelectFunc();
    }
}
